/**
 * Variant A: Full Layout (Original)
 * 4 cards grans amb tota la informació expandida
 * Ideal per desktop, mostra tot sense scroll
 */

import { useTranslation } from 'react-i18next';
import { IconCalendar, IconBed, IconUsers, IconBarcode, IconCheck } from '@tabler/icons-react';
import { Badge } from '@/components/ui/badge';
import { calendarNights, formatCalendarDate } from '@/lib/dates';
import type { ReservationInfoVariantProps } from './types';

export function VariantA({ reserva }: ReservationInfoVariantProps) {
  const { t, i18n } = useTranslation('guestPortal');

  const lang = i18n.resolvedLanguage || i18n.language;
  const nights = calendarNights(reserva.checkIn, reserva.checkOut);

  const getStatusBadge = () => {
    const status = reserva.status || 'CONFIRMED';
    const statusMap: Record<string, { label: string; className: string }> = {
      CONFIRMED: { label: t('reservationInfo.confirmed'), className: 'bg-green-100 text-green-700' },
      PENDING: { label: t('reservationInfo.pending'), className: 'bg-yellow-100 text-yellow-700' },
      CHECKED_IN: { label: t('reservationInfo.checkedIn'), className: 'bg-blue-100 text-blue-700' },
      CHECKED_OUT: { label: t('reservationInfo.checkedOut'), className: 'bg-gray-100 text-gray-700' },
    };
    const statusInfo = statusMap[status] || statusMap.CONFIRMED;
    return <Badge className={statusInfo.className}>{statusInfo.label}</Badge>;
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl shadow-xl p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {t('reservationInfo.title')}
        </h2>
        {getStatusBadge()}
      </div>

      {/* Información principal - 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Check-in */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-green-100 p-3 rounded-xl">
              <IconCalendar className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('reservationInfo.checkIn')}</p>
              <p className="text-lg font-semibold text-gray-900">{formatCalendarDate(reserva.checkIn, lang)}</p>
            </div>
          </div>
        </div>

        {/* Check-out */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-red-100 p-3 rounded-xl">
              <IconCalendar className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('reservationInfo.checkOut')}</p>
              <p className="text-lg font-semibold text-gray-900">{formatCalendarDate(reserva.checkOut, lang)}</p>
            </div>
          </div>
        </div>

        {/* Habitación */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-purple-100 p-3 rounded-xl">
              <IconBed className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('reservationInfo.room')}</p>
              <p className="text-lg font-semibold text-gray-900">{reserva.room?.name || 'N/A'}</p>
              {reserva.room?.type && <p className="text-sm text-gray-500">{reserva.room.type}</p>}
            </div>
          </div>
        </div>

        {/* Huéspedes */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-100 p-3 rounded-xl">
              <IconUsers className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('reservationInfo.guests')}</p>
              <p className="text-lg font-semibold text-gray-900">
                {reserva.numAdults + reserva.numChildren} {t('reservationInfo.guests')}
              </p>
              <p className="text-sm text-gray-500">
                {reserva.numAdults} adultos, {reserva.numChildren} niños
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Información adicional */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{t('reservationInfo.nights', { count: nights })}</span>
            <span className="font-semibold text-gray-900">{nights}</span>
          </div>
        </div>

        {reserva.beds24Id && (
          <div className="bg-white/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                <IconBarcode className="h-4 w-4" />
                {t('reservationInfo.bookingCode')}
              </span>
              <span className="font-mono font-semibold text-gray-900">{reserva.beds24Id}</span>
            </div>
          </div>
        )}
      </div>

      {/* Mensaje de bienvenida */}
      <div className="mt-6 bg-white rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <div className="bg-green-100 p-2 rounded-lg">
            <IconCheck className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 mb-1">{t('welcome')}, {reserva.guest_name}!</p>
            <p className="text-sm text-gray-600">{t('welcomeMessage')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
